import { useCallback, useRef, useState } from 'react';
import { useServiceWorker } from './useServiceWorker';

const PULL_THRESHOLD = 80; // pixels
const PULL_RELEASE_THRESHOLD = 0.5; // 50% of threshold

interface UsePullToRefreshReturn {
  pullDistance: number;
  isRefreshing: boolean;
  handleTouchStart: (e: React.TouchEvent<HTMLDivElement>) => void;
  handleTouchMove: (e: React.TouchEvent<HTMLDivElement>) => void;
  handleTouchEnd: (e: React.TouchEvent<HTMLDivElement>) => void;
}

export function usePullToRefresh(): UsePullToRefreshReturn {
  const { isUpdateAvailable, skipWaiting } = useServiceWorker();
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const touchStartY = useRef(0);
  const isAtTop = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const target = e.currentTarget as HTMLDivElement;
    touchStartY.current = e.touches[0].clientY;
    isAtTop.current = target.scrollTop === 0;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!isAtTop.current || isRefreshing) {
      return;
    }

    const currentY = e.touches[0].clientY;
    const distance = currentY - touchStartY.current;

    if (distance > 0) {
      setPullDistance(distance);
    }
  }, [isRefreshing]);

  const handleTouchEnd = useCallback(
    () => {
      if (!isAtTop.current || isRefreshing) {
        setPullDistance(0);
        return;
      }

      if (pullDistance > PULL_THRESHOLD * PULL_RELEASE_THRESHOLD) {
        setIsRefreshing(true);
        setPullDistance(0);

        // Trigger update if available
        if (isUpdateAvailable) {
          skipWaiting();
        } else {
          // If no update, reset after showing the state briefly
          setTimeout(() => {
            setIsRefreshing(false);
          }, 500);
        }
      } else {
        setPullDistance(0);
      }
    },
    [pullDistance, isRefreshing, isUpdateAvailable, skipWaiting],
  );

  return {
    pullDistance,
    isRefreshing,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
}
